/**
 * What is already saved on the opportunity — design snapshot, product rows and
 * finance partner — so the Products section can tell a pre-linked opportunity
 * apart from one where nothing has been synced yet.
 */
import {
    getRoofSquaresFromSystemDesign,
    type RetrieveSystemDesignResponse,
} from '../../services/oneButtonServices/designServices';
import { countDesignArrayModules } from '../../helpers/productDesignFallbacks';
import {
    parseDesignDataRecord,
    systemSizeKwFromSnapshot,
} from '../../helpers/systemDesignSolarMetrics';
import { preContractHubProductName } from './preContractProductCombinations';
import type { PreContractProduct } from './preContractTypes';

export type PreContractOpportunityLinkLevel = 'none' | 'partial' | 'linked';

export type PreContractOpportunityProductLink = {
    product: PreContractProduct;
    /** Active product row exists on the opportunity. */
    productLinked: boolean;
    /** Finance partner saved on that product row. */
    partner: string | null;
    method: string | null;
    /** Saved design snapshot carries values for this product. */
    designLinked: boolean;
};

export type PreContractOpportunityLink = {
    level: PreContractOpportunityLinkLevel;
    products: PreContractOpportunityProductLink[];
    designLinked: boolean;
    productLinked: boolean;
    partnerLinked: boolean;
    /** Selected products with no active row on the opportunity yet. */
    unlinkedProducts: PreContractProduct[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function positiveNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function isPartnerUnset(partner: string): boolean {
    const text = partner.trim().toLowerCase();
    return !text || text === 'select finance partner';
}

function opportunityProductRows(opportunityDetails: unknown): Record<string, unknown>[] {
    const opp = asRecord(opportunityDetails);
    const raw = opp?.OpportunityProduct ?? opp?.opportunityProduct ?? [];
    if (!Array.isArray(raw)) return [];
    return raw
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => Boolean(row))
        .filter((row) => row.ActiveFlag !== false && row.activeFlag !== false);
}

function findProductRow(
    rows: Record<string, unknown>[],
    product: PreContractProduct,
): Record<string, unknown> | null {
    const hubName = preContractHubProductName(product).toLowerCase();
    return (
        rows.find(
            (row) =>
                String(row.Product_Type_Name ?? row.product_Type_Name ?? '')
                    .trim()
                    .toLowerCase() === hubName,
        ) ?? null
    );
}

function hasSavedSolarDesign(systemDesign: unknown): boolean {
    const sd = asRecord(systemDesign);
    if (!sd) return false;
    const modules = countDesignArrayModules(systemDesign);
    if (modules != null && modules > 0) return true;
    return systemSizeKwFromSnapshot(sd, parseDesignDataRecord(sd)) != null;
}

function hasSavedRoofDesign(systemDesign: unknown): boolean {
    const sd = asRecord(systemDesign);
    if (!sd) return false;
    const roofRaw = sd.Roof ?? sd.RoofOnly;
    const roof = Array.isArray(roofRaw) ? asRecord(roofRaw[0]) : asRecord(roofRaw);
    if (roof) {
        const slopes =
            (positiveNumber(roof.LowSlope) ?? 0) + (positiveNumber(roof.NormalSlope) ?? 0);
        if (slopes > 0) return true;
        if (String(roof.ShingleColor ?? '').trim()) return true;
    }
    return (
        positiveNumber(
            getRoofSquaresFromSystemDesign(systemDesign as RetrieveSystemDesignResponse),
        ) != null
    );
}

function hasSavedBatteryDesign(systemDesign: unknown): boolean {
    const battery = asRecord(asRecord(systemDesign)?.BatteryInformation);
    if (!battery) return false;
    const model = String(battery.Name ?? '').trim();
    return Boolean(model) && positiveNumber(battery.Quantity) != null;
}

function hasSavedDesignForProduct(
    product: PreContractProduct,
    systemDesign: unknown,
): boolean {
    if (product === 'Solar') return hasSavedSolarDesign(systemDesign);
    if (product === 'Battery') return hasSavedBatteryDesign(systemDesign);
    return hasSavedRoofDesign(systemDesign);
}

export function resolvePreContractOpportunityLink(input: {
    opportunityDetails: unknown;
    systemDesign: unknown;
    products: PreContractProduct[];
}): PreContractOpportunityLink {
    const rows = opportunityProductRows(input.opportunityDetails);

    const products = input.products.map((product): PreContractOpportunityProductLink => {
        const row = findProductRow(rows, product);
        const partner = String(
            row?.Finance_Partner_Display_Name ?? row?.finance_Partner_Display_Name ?? '',
        ).trim();
        const method = String(
            row?.Finance_Method_Name ?? row?.finance_Method_Name ?? '',
        ).trim();

        return {
            product,
            productLinked: Boolean(row),
            partner: !partner || isPartnerUnset(partner) ? null : partner,
            method: method || null,
            designLinked: hasSavedDesignForProduct(product, input.systemDesign),
        };
    });

    const designLinked = products.some((entry) => entry.designLinked);
    const productLinked = products.some((entry) => entry.productLinked);
    const partnerLinked = products.some((entry) => entry.partner != null);
    const fullyLinked =
        products.length > 0 &&
        products.every(
            (entry) => entry.productLinked && entry.designLinked && entry.partner != null,
        );

    return {
        level: fullyLinked
            ? 'linked'
            : designLinked || productLinked || partnerLinked
              ? 'partial'
              : 'none',
        products,
        designLinked,
        productLinked,
        partnerLinked,
        unlinkedProducts: products
            .filter((entry) => !entry.productLinked)
            .map((entry) => entry.product),
    };
}
